import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ExperienceVideo } from '@/components/member-experience/ExperienceVideo';
import { safeExperienceDestination } from '@/lib/member-experience';
import type { PopupContent } from '@/services/popups.service';

export function PopupDialog({
  popup,
  open,
  onClose,
  onAction,
  onDismiss,
  preview = false,
  pending = false,
  error,
}: {
  popup: PopupContent;
  open: boolean;
  onClose: () => void;
  onAction?: () => void;
  onDismiss?: () => void;
  preview?: boolean;
  pending?: boolean;
  error?: string;
}) {
  const content = useMemo(
    () =>
      DOMPurify.sanitize(popup.content || '', {
        ALLOWED_TAGS: [
          'p',
          'br',
          'strong',
          'b',
          'em',
          'i',
          'u',
          'h2',
          'h3',
          'ul',
          'ol',
          'li',
          'blockquote',
        ],
        ALLOWED_ATTR: [],
      }),
    [popup.content],
  );
  const destination = safeExperienceDestination(popup.ctaUrl || undefined);
  const image = safeExperienceDestination(popup.imageUrl || undefined);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !pending) (error && onDismiss ? onDismiss : onClose)();
      }}
    >
      <DialogContent data-member-popup className="max-w-xl gap-5 sm:p-8">
        <div className="space-y-2 pr-6">
          <DialogTitle className="text-xl leading-snug">
            {popup.title || 'Prévia do popup'}
          </DialogTitle>
          <DialogDescription>
            {preview
              ? 'Prévia administrativa. Nenhuma visualização será registrada.'
              : 'Um aviso para você.'}
          </DialogDescription>
        </div>
        {popup.contentType === 'IMAGE' && image?.startsWith('https://') && (
          <img
            src={image}
            alt={popup.title}
            className="max-h-[45dvh] w-full rounded-lg object-contain"
          />
        )}
        {popup.contentType === 'VIDEO' && popup.videoUrl && (
          <ExperienceVideo url={popup.videoUrl} title={popup.title} />
        )}
        {content && (
          <div
            className="prose prose-sm dark:prose-invert text-foreground max-w-none whitespace-pre-wrap break-words [&_p]:leading-relaxed"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        )}
        {error && (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        )}
        {destination && popup.ctaText && (
          <div className="space-y-2">
            <Button
              className="w-full"
              disabled={pending}
              onClick={preview ? undefined : onAction}
            >
              {popup.ctaText}
            </Button>
            {preview && (
              <p className="text-muted-foreground break-all text-xs">
                {destination}
              </p>
            )}
          </div>
        )}
        {!preview && (
          <Button variant="ghost" disabled={pending} onClick={onClose}>
            {pending
              ? 'Registrando leitura…'
              : error
                ? 'Tentar fechar novamente'
                : 'Entendi'}
          </Button>
        )}
        {error && onDismiss && (
          <Button variant="ghost" onClick={onDismiss}>
            Fechar sem registrar
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
